import React, { useEffect, useState } from 'react';
import { Package, Pencil, Printer } from 'lucide-react';
import { ItemsDto } from '../../../../../types/items';
import { ItemOverviewTotalsDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../../common/StatusBadge';
import { ImageModal } from '../../picking/modals';
import { useItemImageConfig } from '../../../../../hooks/useItemImageConfig';
import { ITEM_TYPE_STATUS_TYPE } from '../../../../../utils/itemType';
import { formatQty } from '../format';

interface ItemCardHeaderProps {
  item: ItemsDto;
  totals: ItemOverviewTotalsDto;
  onPrintLabel: () => void;
  /** Lagersjef: lenke til Vedlikehold lager (varefelt redigeres der, ikke i varekortet). */
  editHref?: string;
}

type KpiTone = 'neutral' | 'orange' | 'green' | 'blue' | 'red';

const KPI_TONES: Record<KpiTone, { bg: string; text: string }> = {
  neutral: { bg: 'bg-gray-50', text: 'text-gray-900' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
  green: { bg: 'bg-green-50', text: 'text-green-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700' },
  red: { bg: 'bg-red-50', text: 'text-red-700' },
};

const Kpi: React.FC<{ label: string; value: number | null; tone: KpiTone }> = ({ label, value, tone }) => (
  <div className={`${KPI_TONES[tone].bg} p-3 rounded-lg text-center`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-lg font-semibold ${KPI_TONES[tone].text}`}>{formatQty(value)}</div>
  </div>
);

/**
 * Topp av varekortet: bilde (når items.imageBaseUrl + filnavn finnes — profilflagget
 * ShowItemPicureYesNo gjelder plukk/telling og ignoreres her), varenr, beskrivelse,
 * varetype (StatusType 32), EAN og saldo-nøkkeltall.
 */
export const ItemCardHeader: React.FC<ItemCardHeaderProps> = ({ item, totals, onPrintLabel, editHref }) => {
  const { t } = useTranslation();
  const { buildItemImageUrl, configLoaded } = useItemImageConfig();
  const [imageFailed, setImageFailed] = useState(false);
  const [showImage, setShowImage] = useState(false);

  useEffect(() => { setImageFailed(false); }, [item.itemNo]);

  const imageUrl = configLoaded ? buildItemImageUrl(item.itemPictureName) : undefined;
  const hasImage = !!imageUrl && !imageFailed;
  const hasManagerTotals = totals.incoming !== null && totals.incoming !== undefined;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
      <div className="flex items-start gap-3 sm:gap-4">
        {hasImage ? (
          <button
            type="button"
            onClick={() => setShowImage(true)}
            className="flex-shrink-0 w-20 h-20 sm:w-28 sm:h-28 rounded-lg border border-gray-200 overflow-hidden bg-white hover:border-blue-400 transition-colors"
            aria-label={t('warehouse.modules.itemCard.header.showImage', {}, 'Vis varebilde i full størrelse')}
          >
            <img
              src={imageUrl}
              alt={item.itemNo}
              loading="lazy"
              className="w-full h-full object-contain"
              onError={() => setImageFailed(true)}
            />
          </button>
        ) : (
          <div className="flex-shrink-0 bg-blue-100 rounded-full p-3">
            <Package className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{item.itemNo}</h1>
          <p className="text-sm sm:text-base text-gray-600 break-words mt-0.5">
            {item.description1 || t('warehouse.modules.itemCard.header.noDescription', {}, 'Ingen beskrivelse')}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {item.itemType !== null && item.itemType !== undefined && (
              <StatusBadge value={item.itemType} statusType={ITEM_TYPE_STATUS_TYPE} />
            )}
            {item.itemBarCode && (
              <span className="inline-flex items-center text-xs font-mono px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                EAN {item.itemBarCode}
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
          {editHref && (
            <a
              href={editHref}
              className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              title={t('warehouse.modules.itemCard.actions.edit', {}, 'Rediger vare')}
            >
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">{t('warehouse.modules.itemCard.actions.edit', {}, 'Rediger vare')}</span>
            </a>
          )}
          <button
            type="button"
            onClick={onPrintLabel}
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-colors"
            title={t('warehouse.modules.itemCard.actions.printLabel', {}, 'Skriv ut etikett')}
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{t('warehouse.modules.itemCard.actions.printLabel', {}, 'Skriv ut etikett')}</span>
          </button>
        </div>
      </div>

      <div className={`grid gap-2 sm:gap-3 mt-4 ${hasManagerTotals ? 'grid-cols-3 lg:grid-cols-6' : 'grid-cols-3'}`}>
        <Kpi label={t('warehouse.modules.itemCard.totals.physical', {}, 'På lager')} value={totals.physical} tone="neutral" />
        <Kpi label={t('warehouse.modules.itemCard.totals.reserved', {}, 'Reservert')} value={totals.reserved} tone="orange" />
        <Kpi label={t('warehouse.modules.itemCard.totals.available', {}, 'Tilgjengelig')} value={totals.available} tone="green" />
        {hasManagerTotals && (
          <>
            <Kpi label={t('warehouse.modules.itemCard.totals.incoming', {}, 'Innkommende')} value={totals.incoming} tone="neutral" />
            <Kpi label={t('warehouse.modules.itemCard.totals.outgoing', {}, 'I ordre')} value={totals.outgoing} tone="neutral" />
            <Kpi
              label={t('warehouse.modules.itemCard.totals.projected', {}, 'Disponibelt')}
              value={totals.projected}
              tone={(totals.projected ?? 0) < 0 ? 'red' : 'blue'}
            />
          </>
        )}
      </div>

      <ImageModal isOpen={showImage && hasImage} imageUrl={imageUrl || ''} onClose={() => setShowImage(false)} />
    </div>
  );
};
